import { PREDECESSOR_LOCATION_LABEL } from 'components/insights/constants';
import {
  createClientDropdown,
  createGroupTypeDropdown,
  defaultFilterDropdownConfig,
  HEALTH_PLAN_DROPDOWN,
  HOSPITAL_CLIENT_DROPDOWN,
  HOSPITAL_DROPDOWN,
  PLAN_TYPE_DROPDOWN,
  sortFilterDropdowns,
} from 'constants/filterConfigs';
import {
  END_DATE,
  HEALTH_PLAN_CLASSIFICATION,
  HOSPITAL,
  HOSPITAL_CLIENT,
  PLAN_TYPE_CLASSIFICATION,
  PREDECESSOR_GROUP_TYPE,
  PREDECESSOR_LOCATION,
  START_DATE,
} from 'constants/filterKeysConstants';
import { PROVIDER } from 'constants/groupTypes';
import GroupType from 'models/GroupType';
import { useInfiniteLocations } from 'services/api/location';

export const receivingGroupFilterKey = (groupType: GroupType) => groupType.camelCaseApiName;
export const receivingClientFilterKey = (groupType: GroupType) => `${groupType.camelCaseApiName}Client`;

export const filtersForGroupType = (
  filters: Record<string, any>,
  selectedGroupType: GroupType,
  predecessorLocationFilterKey: string,
  predecessorGroupTypeFilterKey?: string
): Record<string, any> => {
  const validKeys = [
    predecessorLocationFilterKey,
    receivingClientFilterKey(selectedGroupType),
    receivingGroupFilterKey(selectedGroupType),
    HEALTH_PLAN_CLASSIFICATION,
    PLAN_TYPE_CLASSIFICATION,
    HOSPITAL,
    HOSPITAL_CLIENT,
    START_DATE,
    END_DATE,
    ...(predecessorGroupTypeFilterKey ? [predecessorGroupTypeFilterKey] : []),
  ];

  return Object.entries(filters).reduce(
    (acc, [key, val]) => (validKeys.includes(key) ? { ...acc, [key]: val } : acc),
    {} as Record<string, any>
  );
};

type GetFilterSectionsOptions = {
  predecessorGroupTypes: GroupType[];
  selectedGroupType: GroupType | undefined;
  predecessorLocationFilterKey?: string;
  predecessorGroupTypeFilterKey?: string;
  showPredecessorGroupTypeFilter?: boolean;
};

export const getFilterSections = ({
  predecessorGroupTypes,
  selectedGroupType,
  predecessorLocationFilterKey = PREDECESSOR_LOCATION,
  predecessorGroupTypeFilterKey = PREDECESSOR_GROUP_TYPE,
  showPredecessorGroupTypeFilter = true,
}: GetFilterSectionsOptions) => {
  if (predecessorGroupTypes.length === 0 || !selectedGroupType) return [];

  const groupTypeName = selectedGroupType.displayName;
  const hasMultiplePredecessorGroupTypes = predecessorGroupTypes.length > 1;

  const predecessorLocationDropdown = {
    ...defaultFilterDropdownConfig,
    placeholder: `Referring ${PREDECESSOR_LOCATION_LABEL}`,
    name: predecessorLocationFilterKey,
    getOptions: {
      hook: useInfiniteLocations,
      params: { sortBy: 'name asc', groupTypeType: PROVIDER },
    },
  };

  const providerClientDropdown = createClientDropdown(
    [selectedGroupType.id],
    `${groupTypeName} Company`,
    receivingClientFilterKey(selectedGroupType),
    { groupTypeType: PROVIDER }
  );

  const groupDropdown = createGroupTypeDropdown(selectedGroupType, undefined, {
    placeholder: groupTypeName,
    name: receivingGroupFilterKey(selectedGroupType),
  });

  const predecessorGroupTypeDropdown = {
    ...defaultFilterDropdownConfig,
    placeholder: 'Level of Care',
    name: predecessorGroupTypeFilterKey,
    options: predecessorGroupTypes.map((gt) => ({ id: gt.id, name: gt.displayName })),
  };

  const showPredecessorGroupTypeDropdown = hasMultiplePredecessorGroupTypes && showPredecessorGroupTypeFilter;

  const dropdowns = [
    predecessorLocationDropdown,
    providerClientDropdown,
    groupDropdown,
    HEALTH_PLAN_DROPDOWN,
    PLAN_TYPE_DROPDOWN,
    ...(showPredecessorGroupTypeDropdown ? [predecessorGroupTypeDropdown] : []),
  ];

  return [
    {
      filters: sortFilterDropdowns(dropdowns),
    },
  ];
};

export const getBusinessDevelopmentFilterSections = (options: GetFilterSectionsOptions) => {
  const sections = getFilterSections({ ...options, showPredecessorGroupTypeFilter: false });
  if (!sections.length) return sections;

  const renamedFilters = sections[0].filters.map((filter) => {
    if (filter.name === PREDECESSOR_LOCATION) {
      return { ...filter, placeholder: PREDECESSOR_LOCATION_LABEL };
    }
    if (filter.name === HEALTH_PLAN_CLASSIFICATION) {
      return { ...filter, placeholder: 'Payer Source' };
    }
    return filter;
  });

  return [
    {
      filters: sortFilterDropdowns([...renamedFilters, HOSPITAL_DROPDOWN, HOSPITAL_CLIENT_DROPDOWN]),
    },
  ];
};
